import React from 'react';
import { css } from '@compiled/react';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { Button, ButtonAppearance, ButtonSize, Heading, Text } from '@atlassian/learning-components';

import { getAssetUrl } from 'src/services/http/user';

interface HeaderProps {
  logoText?: string;
  heading: string;
  subtext: string;
  backgroundImagePath?: string;
  ctaButtonLabel?: string;
  ctaButtonLink?: string;
  ctaButtonOnClick?: () => void;
}

export const HeroHeader: React.FC<HeaderProps> = ({
  logoText,
  heading,
  subtext,
  backgroundImagePath = '/images/homePage/heroDesktopBackground.png',
  ctaButtonLabel,
  ctaButtonLink,
  ctaButtonOnClick,
}) => (
  <section
    data-testid="home-page-heading"
    aria-labelledby="home-page-heading"
    css={containerStyles}
    style={
      {
        '--desktop-hero-bg': `url(${getAssetUrl(backgroundImagePath)})`,
      } as Record<string, string>
    }
  >
    <div css={textContentContainerStyles}>
      {logoText && (
        <div css={logoContainerStyles}>
          <img
            css={css({ marginTop: 0, display: 'flex' })}
            src={getAssetUrl('/images/logos/atlassian-full-logo.svg')}
            alt="atlassian logo"
          />
          <span css={css({ marginLeft: '5px' })}>{logoText}</span>
        </div>
      )}
      <div css={headerContainerStyles}>
        <Heading level={0}>{heading}</Heading>
      </div>
      <div css={subtextStyles}>
        <Text size="large">{subtext}</Text>
      </div>
      {ctaButtonLabel && (
        <div css={ctaButtonContainerStyles}>
          <Button
            appearance={ButtonAppearance.PRIMARY}
            label={ctaButtonLabel}
            href={ctaButtonLink}
            onClick={ctaButtonOnClick}
            size={ButtonSize.LARGE}
            isResponsive={true}
          />
        </div>
      )}
    </div>
  </section>
);

const containerStyles = css({
  width: '100%',
  height: '496px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  textAlign: 'center',
  backgroundPosition: 'bottom',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundImage: 'var(--desktop-hero-bg)',

  [media.below.md]: {
    backgroundImage: 'none',
    height: 'auto',
  },
});

const textContentContainerStyles = css({
  maxWidth: '720px',
  boxSizing: 'content-box',
  padding: `0 ${token('space.300')}`,

  [media.below.md]: {
    padding: `${token('space.600')} ${token('space.300')} 0`,
  },

  [media.below.sm]: {
    padding: `${token('space.600')} ${token('space.200')} 0 !important`,
  },
});

const logoContainerStyles = css({
  fontFamily: 'Charlie Display, sans-serif',
  height: '44px',
  fontSize: '16px',
  lineHeight: '16px',
  fontWeight: 400,
  color: '#000000',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  marginBottom: `${token('space.100')}`,
});

const headerContainerStyles = css({
  marginBottom: 0,

  [media.below.sm]: {
    marginBottom: `${token('space.200')}`,
  },
});

const subtextStyles = css({
  marginTop: '16px',
});

const ctaButtonContainerStyles = css({
  marginTop: `${token('space.400')}`,
  display: 'flex',
  justifyContent: 'center',
});
